/**
 * Users Management API endpoints
 */
import { api } from './baseApi';
import type { User } from '../../types/user';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

interface UserResponse {
  success: boolean;
  message?: string;
  data: User;
}

interface ProfileResponse {
  success: boolean;
  message: string;
  data: User;
}

interface UpdateProfileData {
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  gender?: 'male' | 'female' | '';
  date_of_birth?: string;
  profile_picture?: File | null;
}

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Profile endpoints
    getProfile: builder.query<ProfileResponse, void>({
      query: () => '/users/profile/',
      providesTags: ['User'],
    }),
    updateProfile: builder.mutation<ProfileResponse, UpdateProfileData>({
      query: (data) => {
        const formData = new FormData();
        
        if (data.phone_number) formData.append('phone_number', data.phone_number);
        if (data.first_name) formData.append('first_name', data.first_name);
        if (data.last_name) formData.append('last_name', data.last_name);
        if (data.gender) formData.append('gender', data.gender);
        if (data.date_of_birth) formData.append('date_of_birth', data.date_of_birth);
        if (data.profile_picture) formData.append('profile_picture', data.profile_picture);

        return {
          url: '/users/profile/',
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['User'],
    }),
    
    // User management endpoints
    getUsers: builder.query<PaginatedResponse<User>, { search?: string; page?: number }>({
      query: (params) => ({
        url: '/dashboard/users/',
        params,
      }),
      providesTags: ['Users'],
    }),
    getUser: builder.query<UserResponse, number>({
      query: (id) => `/dashboard/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    updateUser: builder.mutation<UserResponse, { id: number; data: Partial<User> }>({
      query: ({ id, data }) => ({
        url: `/dashboard/users/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'Users'],
    }),
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    activateUser: builder.mutation<UserResponse, number>({
      query: (id) => ({
        url: `/dashboard/users/${id}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'User', id }, 'Users'],
    }),
    deactivateUser: builder.mutation<UserResponse, number>({
      query: (id) => ({
        url: `/dashboard/users/${id}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'User', id }, 'Users'],
    }),
    verifyUser: builder.mutation<UserResponse, number>({
      query: (id) => ({
        url: `/dashboard/users/${id}/verify/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'User', id }, 'Users'],
    }),
  }),
});

export const {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useVerifyUserMutation,
} = usersApi;

